import React, { useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvButton,
  SalvDropdown,
  SalvModal,
  SalvNumberField,
} from '@salvapp/ui-components';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { Form, Formik, FormikHelpers, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useSetFinalRiskRanges } from './finalRiskRangeActions';
import { InternalApiFinalRiskLevel } from '../../api-internal';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface RangeRow {
  level: InternalApiFinalRiskLevel | '';
  scoreFrom: number | undefined;
  scoreTo: number | undefined;
}

interface FormValues {
  ranges: RangeRow[];
}

const riskLevelOptions: DropdownOption[] = Object.values(InternalApiFinalRiskLevel).map((level) => ({
  key: level,
  value: level,
  label: level.replace(/_/g, ' '),
}));

const validationSchema = Yup.object().shape({
  ranges: Yup.array()
    .of(
      Yup.object().shape({
        level: Yup.string().required('Level is required'),
        scoreFrom: Yup.number().required('Score from is required'),
        scoreTo: Yup.number().required('Score to is required'),
      })
    )
    .min(1, 'At least one range is required')
    .required(),
});

const emptyRow: RangeRow = { level: '', scoreFrom: undefined, scoreTo: undefined };

const SetFinalRiskRangesModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useSetFinalRiskRanges();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { ranges: [{ ...emptyRow }] };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        ranges: values.ranges.map((row) => ({
          level: row.level as InternalApiFinalRiskLevel,
          scoreFrom: row.scoreFrom!,
          scoreTo: row.scoreTo!,
        })),
      });
      dispatch(showFeedback({ message: 'Final risk ranges set successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Set Final Risk Ranges</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <FieldArray name='ranges'>
                {({ push, remove }) => (
                  <div>
                    {values.ranges.map((row, index) => (
                      <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12 }}>
                        <SalvDropdown
                          label='Risk Level'
                          options={riskLevelOptions}
                          value={riskLevelOptions.find((o) => o.value === row.level)}
                          onChange={(options: DropdownOption[]) =>
                            setFieldValue(`ranges.${index}.level`, options[0]?.value || '')
                          }
                        />
                        <SalvNumberField
                          label='Score From'
                          value={row.scoreFrom}
                          onChange={(value: number) => setFieldValue(`ranges.${index}.scoreFrom`, value)}
                        />
                        <SalvNumberField
                          label='Score To'
                          value={row.scoreTo}
                          onChange={(value: number) => setFieldValue(`ranges.${index}.scoreTo`, value)}
                        />
                        {values.ranges.length > 1 && (
                          <SalvButton variant='muted' onClick={() => remove(index)} label='Remove' />
                        )}
                      </div>
                    ))}
                    <SalvButton variant='text' onClick={() => push({ ...emptyRow })} label='Add Row' />
                  </div>
                )}
              </FieldArray>
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Set Ranges' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default SetFinalRiskRangesModal;
